import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, RefreshCcw, Search, DownloadCloud, Pencil, Check, X, CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fetchAdminInventory, syncAdminInventory, updateAdminInventory, setAdminInventoryStartDate } from "@/lib/api";

const FILTERS = [
  { v: "all", l: "All" },
  { v: "shipped", l: "Has shipments" },
  { v: "low", l: "Low stock" },
  { v: "out", l: "Out of stock" },
  { v: "inactive", l: "Not in catalogue" },
];

function Stat({ label, value, tone = "zinc", testId }) {
  const tones = {
    zinc: "bg-white border-zinc-200 text-zinc-900",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 min-w-0 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 truncate">{label}</div>
      <div className="text-xl font-bold tabular-nums">{typeof value === "string" ? value : Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function EditableStart({ row, onSave, sfx = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.starting);
  const [date, setDate] = useState(row.start_date);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(row.starting); setDate(row.start_date); }, [row.starting, row.start_date]);
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 tabular-nums font-semibold hover:text-violet-700" title={`Counting shipped orders since ${row.start_date}`} data-testid={`inv-start-${row.product_id}${sfx}`}>
        {row.starting} <Pencil size={11} className="text-zinc-400" />
      </button>
    );
  }
  return (
    <div className="flex flex-col gap-1 items-end" data-testid={`inv-start-edit-${row.product_id}${sfx}`}>
      <input type="number" min={0} value={val} onChange={(e) => setVal(e.target.value)} className="w-20 h-8 rounded-md border border-zinc-300 px-2 text-sm text-right" data-testid={`inv-start-input-${row.product_id}${sfx}`} />
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-[130px] rounded-md border border-zinc-300 px-1.5 text-[11px]" title="Count date" data-testid={`inv-date-input-${row.product_id}${sfx}`} />
      <div className="flex gap-1">
        <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { await onSave({ starting: Number(val), start_date: date }); setEditing(false); } catch { /* toast shown */ } finally { setBusy(false); } }} className="p-1 rounded bg-emerald-600 text-white" data-testid={`inv-start-save-${row.product_id}${sfx}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}</button>
        <button type="button" onClick={() => { setEditing(false); setVal(row.starting); setDate(row.start_date); }} className="p-1 rounded bg-zinc-200 text-zinc-700"><X size={12} /></button>
      </div>
    </div>
  );
}

function Avail({ r }) {
  return (
    <div data-testid={`inv-avail-${r.product_id}`}>
      <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${r.out_of_stock ? "text-rose-600" : r.low_stock ? "text-amber-600" : "text-emerald-700"}`}>
        {(r.out_of_stock || r.low_stock) && <AlertTriangle size={12} />}
        {r.available}
      </span>
      {r.out_of_stock && <div className="text-[10px] text-rose-600 font-semibold">Out of stock</div>}
    </div>
  );
}

export default function InventoryTab({ callerUserRecordId, readOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [allDate, setAllDate] = useState("");
  const [settingDate, setSettingDate] = useState(false);

  async function load(refresh = false) {
    setLoading(true);
    try {
      setData(await fetchAdminInventory(callerUserRecordId, refresh));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load inventory");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (callerUserRecordId) load(); /* eslint-disable-next-line */ }, [callerUserRecordId]);

  async function sync() {
    setSyncing(true);
    try {
      const res = await syncAdminInventory(callerUserRecordId);
      setData(res);
      toast.success(res.added ? `Added ${res.added} product${res.added === 1 ? "" : "s"} to Fulfilment (Starting Inventory 0 — set the count)` : "Already in sync");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  function replaceRow(res, rowId) {
    setData((d) => d && { ...d, totals: res.totals, rows: d.rows.map((r) => (r.id === rowId ? res.row : r)) });
  }
  async function saveRow(row, patch) {
    try {
      replaceRow(await updateAdminInventory(callerUserRecordId, row.id, patch), row.id);
      toast.success("Inventory updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      throw e;
    }
  }
  async function applyAllDate() {
    if (!allDate) return;
    setSettingDate(true);
    try {
      setData(await setAdminInventoryStartDate(callerUserRecordId, allDate));
      toast.success(`Count date set to ${allDate} for all products`);
      setAllDate("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set date");
    } finally {
      setSettingDate(false);
    }
  }

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => {
      if (s && !(r.name.toLowerCase().includes(s) || String(r.product_id).includes(s))) return false;
      if (filter === "shipped") return r.shipped > 0;
      if (filter === "low") return r.low_stock;
      if (filter === "out") return r.out_of_stock;
      if (filter === "inactive") return !r.active;
      return true;
    });
  }, [data, q, filter]);

  const t = data?.totals || {};
  return (
    <div className="space-y-4 min-w-0" data-testid="inventory-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Starting units" value={t.starting} testId="inv-total-starting" />
        <Stat label="Shipped" value={t.shipped} tone="sky" testId="inv-total-shipped" />
        <Stat label="Available" value={t.available} tone="emerald" testId="inv-total-available" />
        <Stat label="MSRP value in stock" value={`$${Number(t.msrp_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="amber" testId="inv-total-msrp" />
      </div>

      {!readOnly && data?.missing_products?.length > 0 && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 flex items-start gap-3" data-testid="inv-missing-banner">
          <DownloadCloud className="text-violet-600 shrink-0 mt-0.5" size={18} />
          <div className="flex-1 min-w-0 text-xs text-violet-900">
            <span className="font-semibold">{data.missing_products.length} active product{data.missing_products.length === 1 ? "" : "s"} not in Fulfilment:</span>{" "}
            {data.missing_products.map((m) => m.name).join(", ")}
          </div>
          <Button size="sm" onClick={sync} disabled={syncing} className="h-8 rounded-lg bg-violet-600 hover:bg-violet-700 shrink-0" data-testid="inv-sync-btn">
            {syncing ? <Loader2 size={14} className="animate-spin" /> : "Sync"}
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product name or Product ID…" className="pl-8 h-10 rounded-xl" data-testid="inv-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          {FILTERS.map((f) => (
            <button key={f.v} type="button" onClick={() => setFilter(f.v)} className={`rounded-full border px-3 h-8 text-xs font-medium ${filter === f.v ? "bg-zinc-900 border-zinc-900 text-white" : "bg-white border-zinc-200 text-zinc-700"}`} data-testid={`inv-filter-${f.v}`}>{f.l}</button>
          ))}
          <button type="button" onClick={() => load(true)} className="rounded-full border border-zinc-200 bg-white h-8 w-8 flex items-center justify-center text-zinc-500" title="Recompute from orders" data-testid="inv-refresh">
            <RefreshCcw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        {!readOnly && <div className="flex items-center gap-2 text-xs text-zinc-600 flex-wrap" data-testid="inv-all-date">
          <CalendarDays size={14} className="text-zinc-400" />
          <span>Count date for all products:</span>
          <input type="date" value={allDate} onChange={(e) => setAllDate(e.target.value)} className="h-8 rounded-md border border-zinc-300 px-1.5 text-xs" data-testid="inv-all-date-input" />
          <Button size="sm" variant="outline" disabled={!allDate || settingDate} onClick={applyAllDate} className="h-8 rounded-lg" data-testid="inv-all-date-apply">
            {settingDate ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
          </Button>
          <span className="text-zinc-400">Shipped orders on/after this date are deducted.</span>
        </div>}
      </div>

      {loading && !data ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-zinc-200 bg-white py-10 text-center text-xs text-zinc-500" data-testid="inv-empty">No products match.</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-2xl border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-xs" data-testid="inv-table">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-2 py-2 font-semibold">ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Product</th>
                  <th className="text-right px-2 py-2 font-semibold">Starting</th>
                  <th className="text-right px-2 py-2 font-semibold">Shipped</th>
                  <th className="text-right px-2 py-2 font-semibold">MSRP value</th>
                  <th className="text-right px-3 py-2 font-semibold">Available</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rows.map((r) => (
                  <tr key={r.id} className={r.out_of_stock ? "bg-rose-50/60" : ""} data-testid={`inv-row-${r.product_id}`}>
                    <td className="px-3 py-2 tabular-nums text-zinc-500 align-top">{r.product_id}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <div className="h-10 w-10 rounded-lg bg-zinc-50 border border-zinc-200 overflow-hidden shrink-0">
                          {r.image_url && <img src={r.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-900 truncate max-w-[160px]">{r.name}</div>
                          <div className="text-[11px] text-zinc-500 flex gap-2 flex-wrap max-w-[160px]">
                            {r.case_breakdown && <span>Cases: {r.case_breakdown}</span>}
                            <span>since {r.start_date}</span>
                            {!r.active && <span className="text-zinc-400">· not in catalogue</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right align-top">{readOnly ? <span className="tabular-nums font-semibold" data-testid={`inv-start-${r.product_id}`}>{r.starting}</span> : <EditableStart row={r} onSave={(p) => saveRow(r, p)} />}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-sky-700 align-top" data-testid={`inv-shipped-${r.product_id}`}>{r.shipped}</td>
                    <td className="px-2 py-2 text-right align-top whitespace-nowrap" data-testid={`inv-msrp-${r.product_id}`}><div className="text-base font-bold tabular-nums text-amber-800">${Number(r.msrp_value || 0).toFixed(2)}</div><div className="text-[10px] text-zinc-400">@ ${Number(r.msrp || 0).toFixed(2)}</div></td>
                    <td className="px-3 py-2 text-right align-top"><Avail r={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden space-y-2" data-testid="inv-cards">
            {rows.map((r) => (
              <li key={r.id} className={`rounded-xl border p-3 ${r.out_of_stock ? "border-rose-200 bg-rose-50/60" : "border-zinc-200 bg-white"}`} data-testid={`inv-card-${r.product_id}`}>
                <div className="flex items-start gap-2.5">
                  <div className="h-11 w-11 rounded-lg bg-zinc-50 border border-zinc-200 overflow-hidden shrink-0">
                    {r.image_url && <img src={r.image_url} alt="" className="h-full w-full object-cover" loading="lazy" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-zinc-900 text-sm leading-tight">{r.name}</div>
                    <div className="text-[11px] text-zinc-500">ID {r.product_id}{r.case_breakdown ? ` · Cases ${r.case_breakdown}` : ""} · since {r.start_date}</div>
                  </div>
                  <div className="text-right"><div className="text-[10px] uppercase tracking-widest text-zinc-400">Avail.</div><Avail r={r} /></div>
                </div>
                <div className="mt-2 rounded-lg bg-amber-50 px-2 py-1.5 flex items-center justify-between"><span className="text-[10px] uppercase tracking-widest text-amber-700">MSRP value</span><span className="text-base font-bold tabular-nums text-amber-800">${Number(r.msrp_value || 0).toFixed(2)} <span className="text-[10px] font-normal text-amber-700/70">@ ${Number(r.msrp || 0).toFixed(2)}</span></span></div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-lg bg-zinc-50 px-2 py-1.5"><div className="text-[10px] uppercase tracking-widest text-zinc-400">Start</div>{readOnly ? <span className="tabular-nums font-semibold">{r.starting}</span> : <EditableStart row={r} onSave={(p) => saveRow(r, p)} sfx="-m" />}</div>
                  <div className="rounded-lg bg-sky-50 px-2 py-1.5"><div className="text-[10px] uppercase tracking-widest text-sky-600">Shipped</div><div className="font-semibold tabular-nums text-sky-800">{r.shipped}</div></div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {data?.computed_at && (
        <p className="text-[11px] text-zinc-400" data-testid="inv-computed-at">
          Available = Starting − Shipped. Shipped = units on Completed orders since the count date plus orders picked under a dealer in the Fulfillment Fees tab (bundles deduct each component); cancelled orders never count. Written back to the Airtable Fulfilment table. Updated {new Date(data.computed_at).toLocaleTimeString()}.
        </p>
      )}
    </div>
  );
}
